/**
 * Network Quality Indicator Component
 * Displays network quality with animated WiFi icon
 */

import React from "react";
import { Wifi, WifiOff, WifiLow, WifiHigh } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NetworkQuality } from "@/types/video-call";
import { Badge } from "@/components/ui/badge";

interface NetworkQualityIndicatorProps {
  quality: NetworkQuality;
  className?: string;
}

const getNetworkConfig = (quality: NetworkQuality) => {
  switch (quality) {
    case "excellent":
      return {
        icon: WifiHigh,
        variant: "success" as const,
        label: "Excellent network",
      };
    case "good":
      return {
        icon: WifiHigh,
        variant: "success" as const,
        label: "Good network",
      };
    case "poor":
      return {
        icon: WifiLow,
        variant: "warning" as const,
        label: "Poor network",
      };
    case "bad":
      return {
        icon: WifiLow,
        variant: "warning" as const,
        label: "Bad network",
      };
    case "very-bad":
      return {
        icon: WifiOff,
        variant: "critical" as const,
        label: "Very bad network",
      };
    case "down":
      return {
        icon: WifiOff,
        variant: "critical" as const,
        label: "Disconnected",
      };
    case "unknown":
    default:
      return {
        icon: Wifi,
        variant: "neutral" as const,
        label: "Checking network",
      };
  }
};

export const NetworkQualityIndicator: React.FC<
  NetworkQualityIndicatorProps
> = ({ quality, className }) => {
  const { icon: Icon, variant, label } = getNetworkConfig(quality);

  return (
    <Badge
      variant={variant}
      className={cn("gap-1 rounded-full px-2 py-1 text-xs", className)}
      aria-label={label}
      title={label}
    >
      <Icon className="h-3.5 w-3.5" />
      <span className="sr-only sm:not-sr-only">{label}</span>
    </Badge>
  );
};

export default NetworkQualityIndicator;
